// src/components/admin/TorreDeControl/DetailModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Calendar,
  DollarSign,
  Package as PackageIcon,
  MessageSquare,
  Clock,
  Eye,
  Truck,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { io } from 'socket.io-client';

// Import modals
import ActionButtons from './ActionButtons';
import LocationModal from './modals/LocationModal';
import RecipientModal from './modals/RecipientModal';
import DataModal from './modals/DataModal';
import PaymentsModal from './modals/PaymentsModal';
import PostponeModal from './modals/PostponeModal';
import StatusModal from './modals/StatusModal';
import DriverModal from './modals/DriverModal';
import WhatsAppModal from './modals/WhatsAppModal';
import DeliveryCommunicationPanel from '../../shared/communication/DeliveryCommunicationPanel';

const API_URL = process.env.REACT_APP_API_URL;

function DetailModal({ itemId, itemType, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeModal, setActiveModal] = useState(null);
  const mapInstanceRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapIdRef = useRef(`detail-map-${Date.now()}-${Math.random()}`);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchDetails();
    
    // Initialize Socket.IO connection
    socketRef.current = io(API_URL);
    
    // Listen for updates
    if (itemType === 'delivery') {
      socketRef.current.on('delivery_updated', handleDeliveryUpdate);
    } else {
      socketRef.current.on('order_updated', handleOrderUpdate);
    }
    
    return () => {
      cleanupMap();
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [itemId, itemType]);

  useEffect(() => {
    if (data && (data.latitude || data.address?.latitude)) {
      initializeMap();
    }

    return () => {
      cleanupMap();
    };
  }, [data]);

  function handleDeliveryUpdate(updateData) {
    if (updateData.delivery_id === itemId) {
      setData(updateData.data);
    }
  }

  function handleOrderUpdate(updateData) {
    if (updateData.order_id === itemId) {
      setData(updateData.data);
    }
  }

  function cleanupMap() {
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      } catch (err) {
        console.log('Map cleanup error (non-critical):', err);
      }
    }
  }

  async function fetchDetails() {
    setIsLoading(true);
    setError(null);

    try {
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/admin/delivery-point/${itemId}`
          : `${API_URL}/api/admin/order/${itemId}`;

      const response = await axios.get(endpoint);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching details:', err);
      setError('No se pudo cargar la información');
    } finally {
      setIsLoading(false);
    }
  }

  function initializeMap() {
    cleanupMap();

    const lat = itemType === 'delivery' ? data.latitude : data.address?.latitude;
    const lng = itemType === 'delivery' ? data.longitude : data.address?.longitude;

    if (!lat || !lng) return;

    setTimeout(() => {
      const container = document.getElementById(mapIdRef.current);
      if (!container) {
        console.log('Map container not found');
        return;
      }

      if (container._leaflet_id) {
        console.log('Removing existing map from container');
        container._leaflet_id = null;
      }

      try {
        const map = L.map(mapIdRef.current, {
          center: [lat, lng],
          zoom: 16,
          zoomControl: true,
          scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
        }).addTo(map);

        const icon = L.divIcon({
          className: 'custom-detail-marker',
          html: `
            <div style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background-color: #B1336E;
              border: 4px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              <div style="
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: white;
              "></div>
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });

        L.marker([lat, lng], { icon }).addTo(map);
        mapInstanceRef.current = map;
      } catch (err) {
        console.error('Error initializing map:', err);
      }
    }, 100);
  }

  async function handleToggleSubmitted() {
    try {
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/admin/delivery-point/${itemId}/submitted`
          : `${API_URL}/api/admin/order/${itemId}/submitted`;

      const response = await axios.put(endpoint, {
        is_submitted: !data.is_submitted,
      });

      setData((prev) => ({
        ...prev,
        is_submitted: response.data.is_submitted,
      }));
    } catch (error) {
      console.error('Error toggling submitted:', error);
      alert('Error al cambiar estado de submitted');
    }
  }

  function handleActionClick(actionId) {
    switch (actionId) {
      case 'location':
        setActiveModal('location');
        break;
      case 'recipient':
        setActiveModal('recipient');
        break;
      case 'delivery-data':
      case 'order-data':
        setActiveModal('data');
        break;
      case 'payments':
        setActiveModal('payments');
        break;
      case 'postpone':
        setActiveModal('postpone');
        break;
      case 'whatsapp':
        // TODO: Implement WhatsApp functionality
        setActiveModal('whatsapp');
        break;
      case 'submitted':
        handleToggleSubmitted();
        break;
      case 'status':
        setActiveModal('status');
        break;
      case 'driver':
        setActiveModal('driver');
        break;
      default:
        break;
    }
  }

  function getStatusColor(status) {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encamino: 'bg-blue-100 text-blue-800 border-blue-300',
      entregado: 'bg-green-100 text-green-800 border-green-300',
      completado: 'bg-green-100 text-green-800 border-green-300',
      cancelado: 'bg-gray-100 text-gray-800 border-gray-300',
      rechazado: 'bg-red-100 text-red-800 border-red-300',
      postergado: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En camino',
      entregado: 'Entregado',
      completado: 'Completado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
      postergado: 'Postergado',
    };
    return labels[status] || status;
  }

  function getPackageStatusLabel(status) {
    const labels = {
      in_warehouse: 'En tienda',
      in_fastmax: 'En Fast Max',
      out_for_delivery: 'En reparto',
      delivered: 'Entregado',
      returned: 'Devuelto',
      lost_or_damaged: 'Perdido/Dañado',
    };
    return labels[status] || status;
  }

  function getPackageStatusColor(status) {
    const colors = {
      in_warehouse: 'bg-slate-100 text-slate-700',
      in_fastmax: 'bg-blue-100 text-blue-700',
      out_for_delivery: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      returned: 'bg-orange-100 text-orange-700',
      lost_or_damaged: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatLocationActor(locationUpdate) {
    if (!locationUpdate) return 'Sin registro';

    const actorLabels = {
      admin: 'Admin',
      provider: 'Proveedor',
      customer: 'Cliente',
      final_client: 'Cliente final',
      system: 'Sistema',
    };
    const actorLabel = actorLabels[locationUpdate.actor_type] || locationUpdate.actor_type || 'Desconocido';

    if (locationUpdate.actor_username) {
      return `${actorLabel} - ${locationUpdate.actor_username}`;
    }

    return actorLabel;
  }

  function handleWhatsAppClick(phone) {
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/51${cleanPhone}`, '_blank');
  }

  function handleViewUserDetails() {
    // Get the user_id from the data
    const userId = isDelivery ? data.user_id : data.customer_id;
    
    if (!userId) {
      console.warn('No user ID available');
      return;
    }

    // Dispatch custom event to open UserDetailModal
    const event = new CustomEvent('openUserDetailModal', {
      detail: { userId }
    });
    window.dispatchEvent(event);
  }

  function nextImage() {
    if (!data?.image_urls || data.image_urls.length === 0) return;
    setCurrentImageIndex((prev) =>
      prev === data.image_urls.length - 1 ? 0 : prev + 1
    );
  }

  function prevImage() {
    if (!data?.image_urls || data.image_urls.length === 0) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? data.image_urls.length - 1 : prev - 1
    );
  }

  function handleClose() {
    cleanupMap();
    onClose();
  }

  async function handleDelete() {
    const label = itemType === 'delivery' ? 'entrega' : 'recojo';
    const confirmed = window.confirm(
      `Esta accion no se puede deshacer. Estas seguro de que deseas eliminar este ${label}?`
    );

    if (!confirmed) return;

    setIsDeleting(true);

    try {
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/delete_delivery_point/${itemId}`
          : `${API_URL}/api/delete_order/${itemId}`;

      await axios.delete(endpoint);
      onDeleted?.(itemId, itemType);
      handleClose();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert(`Error al eliminar el ${label}`);
    } finally {
      setIsDeleting(false);
    }
  }

  function handleModalClose() {
    setActiveModal(null);
  }

  function handleUpdate() {
    fetchDetails();
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto"></div>
          <p className="mt-4 text-slate-600 font-montserrat">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <p className="text-red-600 mb-4 font-montserrat">{error || 'Error al cargar datos'}</p>
          <button
            onClick={handleClose}
            className="w-full bg-[#B1336E] text-white px-4 py-2 rounded-lg hover:bg-[#B1336E]/90 font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  const isDelivery = itemType === 'delivery';
  const images = data.image_urls || [];
  const hasLocation = isDelivery
    ? data.latitude && data.longitude
    : data.address?.latitude && data.address?.longitude;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-[#B1336E] px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {isDelivery ? (
                <MapPin className="w-5 h-5 text-white" />
              ) : (
                <PackageIcon className="w-5 h-5 text-white" />
              )}
              <div>
                <h2 className="text-white font-bold text-lg font-montserrat">
                  {isDelivery ? 'Entrega' : 'Recojo'} #{data.id}
                </h2>
                {isDelivery && data.recipient_name && (
                  <p className="text-white/80 text-sm font-montserrat">
                    {data.recipient_name}
                  </p>
                )}
              </div>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="ml-4 flex items-center gap-2 bg-red-500/90 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium font-montserrat"
                title={isDelivery ? 'Eliminar entrega' : 'Eliminar recojo'}
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              title="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Images and Map */}
              <div className="lg:col-span-1 space-y-4">
                {/* Image Carousel */}
                {images.length > 0 ? (
                  <div className="relative bg-slate-100 rounded-lg overflow-hidden aspect-video">
                    <img
                      src={images[currentImageIndex]}
                      alt={`Imagen ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-montserrat">
                          {currentImageIndex + 1} / {images.length}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-lg aspect-video flex items-center justify-center">
                    <p className="text-slate-400 font-montserrat">Sin imágenes</p>
                  </div>
                )}

                {/* Map */}
                {hasLocation ? (
                  <div className="rounded-lg overflow-hidden border border-slate-200">
                    <div
                      id={mapIdRef.current}
                      ref={mapContainerRef}
                      className="w-full h-64"
                    ></div>
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-lg h-64 flex items-center justify-center">
                    <p className="text-slate-400 font-montserrat">Sin ubicación disponible</p>
                  </div>
                )}

                {/* Action Buttons */}
                <ActionButtons itemType={itemType} onActionClick={handleActionClick} />
              </div>

              {/* Middle & Right Columns - Details */}
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Badge */}
                <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium font-montserrat ${getStatusColor(
                      data.status
                    )}`}
                  >
                    {getStatusLabel(data.status)}
                  </span>
                  {isDelivery && data.slot && (
                    <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-sm font-montserrat">
                      Prioridad {data.slot}
                    </span>
                  )}
                  <span
                    className={`px-3 py-1 rounded-lg text-sm font-medium font-montserrat ${
                      data.is_submitted
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-slate-100 text-slate-700 border border-slate-300'
                    }`}
                  >
                    {data.is_submitted ? '✓ Submitted' : '○ No Submitted'}
                  </span>
                </div>

                {/* User Info Section */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                      <User className="w-4 h-4" />
                      Información del {isDelivery ? 'Destinatario' : 'Cliente'}
                    </h3>
                    <button
                      onClick={handleViewUserDetails}
                      className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
                      title="Ver detalles del usuario"
                    >
                      <Eye className="w-4 h-4 text-[#B1336E]" />
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-600 font-montserrat">Usuario:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {isDelivery ? data.user_name : data.customer_name}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-600 font-montserrat">Nombre:</span>
                      <span className="ml-2 font-medium text-slate-800 font-montserrat">
                        {isDelivery ? data.recipient_name : data.customer_name}
                      </span>
                    </div>
                    {isDelivery && data.recipient_dni && (
                      <div>
                        <span className="text-slate-600 font-montserrat">DNI:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {data.recipient_dni}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-600 font-montserrat">Teléfono:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {isDelivery ? data.recipient_phone : data.telefono}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          handleWhatsAppClick(
                            isDelivery ? data.recipient_phone : data.telefono
                          )
                        }
                        className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm font-montserrat"
                      >
                        <Phone className="w-4 h-4" />
                        WhatsApp
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Delivery Info Section */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                    <Calendar className="w-4 h-4" />
                    Información de {isDelivery ? 'Entrega' : 'Recojo'}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-slate-600 font-montserrat">Fecha programada:</span>
                      <span className="ml-2 font-medium text-slate-800 font-montserrat">
                        {formatDate(
                          isDelivery ? data.delivery_date : data.recojo_date
                        )}
                      </span>
                    </div>
                    {((isDelivery && data.date_time_entrega) ||
                      (!isDelivery && data.date_time_recojo)) && (
                      <div>
                        <span className="text-slate-600 font-montserrat">Hora real:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {formatTime(
                            isDelivery
                              ? data.date_time_entrega
                              : data.date_time_recojo
                          )}
                        </span>
                      </div>
                    )}
                    {data.arrival_time && (
                      <div>
                        <span className="text-slate-600 font-montserrat">Hora de llegada:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {formatTime(data.arrival_time)}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-slate-600 font-montserrat">Dirección:</span>
                      <span className="ml-2 font-medium text-slate-800 font-montserrat">
                        {isDelivery
                          ? data.address
                          : data.address?.address || 'N/A'}
                      </span>
                    </div>
                    {isDelivery && data.district && (
                      <div>
                        <span className="text-slate-600 font-montserrat">Distrito:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {data.district}
                        </span>
                      </div>
                    )}
                    {isDelivery && (
                      <>
                        <div>
                          <span className="text-slate-600 font-montserrat">Ubicacion actualizada:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            {data.latest_location_update?.created_at
                              ? formatDate(data.latest_location_update.created_at)
                              : 'Sin registro'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-600 font-montserrat">Actualizada por:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            {formatLocationActor(data.latest_location_update)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Financial Info (Deliveries only) */}
                {isDelivery && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                      <DollarSign className="w-4 h-4" />
                      Información Financiera
                    </h3>
                    <div className="space-y-2 text-sm">
                      {data.charge !== null && data.charge !== undefined && (
                        <div>
                          <span className="text-slate-600 font-montserrat">Cargo:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            S/ {data.charge.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {data.charged !== null && data.charged !== undefined && (
                        <div>
                          <span className="text-slate-600 font-montserrat">Cobrado:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            S/ {data.charged.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {data.fastmax_transfer_amount !== null && data.fastmax_transfer_amount !== undefined && (
                        <div>
                          <span className="text-slate-600 font-montserrat">
                            Transfer Fast Max:
                          </span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            S/ {data.fastmax_transfer_amount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {data.provider_transfer_amount !== null && data.provider_transfer_amount !== undefined && (
                        <div>
                          <span className="text-slate-600 font-montserrat">
                            Transfer Proveedor:
                          </span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            S/ {data.provider_transfer_amount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {data.fastmax_exchange_amount !== null && data.fastmax_exchange_amount !== undefined && (
                        <div>
                          <span className="text-slate-600 font-montserrat">Vuelto:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            S/ {data.fastmax_exchange_amount.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Driver Info */}
                {data.driver_id && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                      <Truck className="w-4 h-4" />
                      Conductor
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-slate-600 font-montserrat">Nombre:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {data.driver?.username || `ID ${data.driver_id}`}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-600 font-montserrat">Código:</span>
                        <span className="ml-2 font-medium text-slate-800 font-montserrat">
                          {data.driver_id}
                        </span>
                      </div>
                      {data.sequence && (
                        <div>
                          <span className="text-slate-600 font-montserrat">Secuencia:</span>
                          <span className="ml-2 font-medium text-slate-800 font-montserrat">
                            {data.sequence}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Product Info (Deliveries only) */}
                {isDelivery && data.product && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3 md:col-span-2">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                      <PackageIcon className="w-4 h-4" />
                      Producto
                    </h3>
                    <p className="text-sm text-slate-800 font-montserrat">{data.product}</p>
                    {data.tracking_code && (
                      <div className="text-sm">
                        <span className="text-slate-600 font-montserrat">Código de seguimiento:</span>
                        <span className="ml-2 font-mono font-medium text-slate-800">
                          {data.tracking_code}
                        </span>
                      </div>
                    )}
                    {data.package_status && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-600 text-sm font-montserrat">
                          Estado del paquete:
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded font-montserrat ${getPackageStatusColor(
                            data.package_status
                          )}`}
                        >
                          {getPackageStatusLabel(data.package_status)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes Section */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3 md:col-span-2">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 font-montserrat">
                    <MessageSquare className="w-4 h-4" />
                    Notas
                  </h3>
                  <div className="space-y-3 text-sm">
                    {((isDelivery && data.notes) ||
                      (!isDelivery && data.order_notes)) && (
                      <div>
                        <span className="text-slate-600 block mb-1 font-montserrat">
                          Notas del cliente:
                        </span>
                        <p className="text-slate-800 bg-white p-2 rounded border border-slate-200 font-montserrat">
                          {isDelivery ? data.notes : data.order_notes}
                        </p>
                      </div>
                    )}
                    {((isDelivery && data.notes_driver) ||
                      (!isDelivery && data.driver_notes)) && (
                      <div>
                        <span className="text-slate-600 block mb-1 font-montserrat">
                          Notas del motorizado:
                        </span>
                        <p className="text-slate-800 bg-white p-2 rounded border border-slate-200 font-montserrat">
                          {isDelivery ? data.notes_driver : data.driver_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {isDelivery && (
                  <div className="md:col-span-2 h-[380px] min-h-[320px] max-h-[55vh] sm:h-[460px]">
                    <DeliveryCommunicationPanel
                      deliveryPointId={data.id}
                      currentUserId={2}
                      currentUserType="admin"
                      className="h-full min-h-0"
                      autoScrollOnInitialLoad={false}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end">
            <button
              onClick={handleClose}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {activeModal === 'location' && (
        <LocationModal
          item={data}
          itemType={itemType}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'recipient' && (
        <RecipientModal
          item={data}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'data' && (
        <DataModal
          item={data}
          itemType={itemType}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'payments' && (
        <PaymentsModal
          item={data}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'postpone' && (
        <PostponeModal
          item={data}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'status' && (
        <StatusModal
          item={data}
          itemType={itemType}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'driver' && (
        <DriverModal
          item={data}
          itemType={itemType}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}
      {activeModal === 'whatsapp' && (
        <WhatsAppModal
          item={data}
          itemType={itemType}
          onClose={handleModalClose}
          userId={2} // Admin user ID
        />
      )}
    </>
  );
}

export default DetailModal;
